import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useLocation } from 'react-router-dom'

// Hooks
import { useMaxWidth } from '@tracklib/kit'
import { useWindowSize } from 'hooks/usewindowsize'

import styles from './animationwrapper.css'

type AnimationWrapperProps = {
  children: (expanded: boolean, setExpanded: (exp: boolean) => void) => JSX.Element
}

const AnimationWrapper = ({ children }: AnimationWrapperProps): JSX.Element => {
  const isSmall = useMaxWidth()
  const [expanded, setExpanded] = useState(false)

  const spring = {
    type: 'spring',
    stiffness: 300,
    damping: 40,
    mass: 1,
    velocity: 0,
  }
  const { height = 0 } = useWindowSize(expanded)
  const y = 0
  const expandedY = height - 56 - 4

  const location = useLocation()
  useEffect(() => {
    return setExpanded(false)
  }, [location])

  // Don't animate anything on desktop
  if (!isSmall) {
    return <div className={styles.wrapper}>{children(expanded, setExpanded)}</div>
  }

  // In order to prevent the player (y drag) from moving when dragging the cover (x drag) we need to match the drag direction
  // for both elements during dragging. This will stop drag handler on the player (true = y and x).
  const dragDirection = 'y'

  return (
    <>
      <motion.div
        className={expanded ? styles.backdropExpanded : styles.backdrop}
        initial={{ opacity: 0 }}
        animate={{ opacity: expanded ? 1 : 0 }}
      />
      <motion.div
        animate={isSmall ? { y: expanded ? -expandedY : y } : {}}
        initial={isSmall ? { y } : {}}
        transition={spring}
        className={expanded ? styles.wrapperExpanded : styles.wrapper}
        drag={isSmall ? dragDirection : false}
        dragConstraints={expanded ? { top: -expandedY, bottom: -expandedY } : { bottom: y }}
        onDragEnd={(event, info) => {
          if (expanded && info.offset.y > 0) {
            return setExpanded(false)
          }
          return setExpanded(true)
        }}
      >
        {children(expanded, setExpanded)}
      </motion.div>
    </>
  )
}

export default AnimationWrapper
